import React, { useState, useEffect, useRef } from 'react';
import { Search, Menu, X } from 'lucide-react';
import logoImg from '../assets/logo.png';
import API_ROUTES from './config/apiConfig';
import { getPublicRequest } from '../api/auth';

const API_BASE_URL = 'https://lightingale.co.in/api';
const BASE_HOST_URL = 'https://lightingale.co.in';

const SAFE_PLACEHOLDER_SVG = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50"><rect width="50" height="50" fill="%23222222"/><text x="50%" y="50%" fill="%23888888" font-size="10" font-family="sans-serif" text-anchor="middle" dominant-baseline="middle">Lamps</text></svg>`;

export default function Header({
  onNavigate,
  products = [],
  categories: propCategories = [],
  subcategories: propSubcategories = []
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchableItems, setSearchableItems] = useState([]);

  const searchContainerRef = useRef(null);
  const mobileInputRef = useRef(null);

  useEffect(() => {
    if (isMobileSearchOpen && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [isMobileSearchOpen]);

  const getItemName = (item) =>
    item?.name ||
    item?.title ||
    item?.categoryName ||
    item?.subCategoryName ||
    item?.label ||
    "Unnamed";

  const getItemImage = (item) => {
    let imgPath =
      item?.primaryImage?.url ||
      item?.primaryImage ||
      item?.image?.url ||
      item?.image ||
      item?.images?.[0]?.url ||
      item?.images?.[0] ||
      item?.imageUrl ||
      item?.bannerImage ||
      "";

    if (typeof imgPath !== 'string' || !imgPath.trim()) {
      return SAFE_PLACEHOLDER_SVG;
    }

    if (imgPath.startsWith("http://") || imgPath.startsWith("https://") || imgPath.startsWith("data:")) {
      return imgPath;
    }

    const cleanPath = imgPath.startsWith("/") ? imgPath : `/${imgPath}`;
    return `${BASE_HOST_URL}${cleanPath}`;
  };

  const catKey = JSON.stringify(propCategories);
  const subKey = JSON.stringify(propSubcategories);
  const prodKey = JSON.stringify(products);

  useEffect(() => {
    let isMounted = true;

    const fetchAndBuildIndex = async () => {
      let rawCats = [...propCategories];

      if (rawCats.length === 0) {
        try {
          const catRoute = API_ROUTES?.GET_CATEGORIES || `${API_BASE_URL}/categories`;
          const catRes = await getPublicRequest(catRoute);
          rawCats = Array.isArray(catRes?.data)
            ? catRes.data
            : catRes?.data?.items || catRes?.data?.data || (Array.isArray(catRes) ? catRes : []);
        } catch (err) {
          // Safe fallback
        }
      }

      if (!isMounted) return;

      const activeCats = rawCats.filter((item) => item.isActive !== false);
      setCategories(activeCats);

      const indexList = [];

      rawCats.forEach((cat) => {
        indexList.push({
          _id: cat._id || cat.id || cat.slug,
          name: getItemName(cat),
          code: "Category",
          type: "category",
          slug: cat.slug || cat._id,
          img: getItemImage(cat),
        });

        const nestedSubs = [
          ...(cat.subCategories || []),
          ...(cat.subcategories || []),
          ...(cat.children || []),
          ...(cat.items || []),
          ...(cat.subs || [])
        ];

        nestedSubs.forEach((sub) => {
          if (typeof sub === 'object' && sub !== null) {
            indexList.push({
              _id: sub._id || sub.id || sub.slug,
              name: getItemName(sub),
              code: "Collection",
              type: "subcategory",
              slug: sub.slug || sub._id || cat.slug,
              img: getItemImage(sub) || getItemImage(cat),
            });

            const subProds = sub.products || sub.items || [];
            if (Array.isArray(subProds)) {
              subProds.forEach((p) => {
                indexList.push({
                  _id: p._id || p.id,
                  name: getItemName(p),
                  code: p.productCode || p.sku || p.code || "",
                  type: "product",
                  img: getItemImage(p),
                  material: p.rawMaterial?.name || p.material || "",
                });
              });
            }
          }
        });

        const catProds = cat.products || [];
        if (Array.isArray(catProds)) {
          catProds.forEach((p) => {
            indexList.push({
              _id: p._id || p.id,
              name: getItemName(p),
              code: p.productCode || p.sku || p.code || "",
              type: "product",
              img: getItemImage(p),
              material: p.rawMaterial?.name || p.material || "",
            });
          });
        }
      });

      if (Array.isArray(propSubcategories)) {
        propSubcategories.forEach((sub) => {
          indexList.push({
            _id: sub._id || sub.id || sub.slug,
            name: getItemName(sub),
            code: "Collection",
            type: "subcategory",
            slug: sub.slug || sub._id,
            img: getItemImage(sub),
          });
        });
      }

      if (Array.isArray(products)) {
        products.forEach((p) => {
          indexList.push({
            _id: p._id || p.id,
            name: getItemName(p),
            code: p.productCode || p.sku || p.code || "",
            type: "product",
            img: getItemImage(p),
            material: p.rawMaterial?.name || p.material || "",
          });

          if (p.subCategory || p.subcategory) {
            const subObj = p.subCategory || p.subcategory;
            const subName = typeof subObj === 'string' ? subObj : getItemName(subObj);
            if (subName) {
              indexList.push({
                _id: subObj._id || subObj.id || subName,
                name: subName,
                code: "Collection",
                type: "subcategory",
                slug: subObj.slug || p.category?.slug || p.category,
                img: getItemImage(p),
              });
            }
          }
        });
      }

      const uniqueMap = new Map();
      indexList.forEach((item) => {
        if (item.name && item.name !== "Unnamed") {
          const key = `${item.type}-${(item.name).toLowerCase().trim()}`;
          if (!uniqueMap.has(key)) {
            uniqueMap.set(key, item);
          }
        }
      });

      if (isMounted) {
        setSearchableItems(Array.from(uniqueMap.values()));
      }
    };

    fetchAndBuildIndex();

    return () => {
      isMounted = false;
    };
  }, [catKey, subKey, prodKey]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim().toLowerCase();

    if (!trimmedQuery) {
      setSuggestions([]);
      return;
    }

    const matches = searchableItems.filter((item) => {
      const nameMatch = item.name?.toLowerCase().includes(trimmedQuery);
      const codeMatch = item.code?.toLowerCase().includes(trimmedQuery);
      const materialMatch = item.material?.toLowerCase().includes(trimmedQuery);
      return nameMatch || codeMatch || materialMatch;
    });

    setSuggestions(matches.slice(0, 8));
  }, [searchQuery, searchableItems]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsSearchFocused(false);
        setIsMobileSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectSuggestion = (item) => {
    setSearchQuery("");
    setSuggestions([]);
    setIsSearchFocused(false);
    setIsMobileSearchOpen(false);

    if (item.type === "category" || item.type === "subcategory") {
      onNavigate("subcategory", { slug: item.slug || item._id });
    } else {
      onNavigate("pdp", item);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
    }
  };

  return (
    <header style={{ position: 'relative', zIndex: 1000 }} ref={searchContainerRef}>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container top-bar-flex">
          <div className="top-bar-left">
            <span>📞 +91 9350824043</span>
            <span>✉️ bkksaj@gmail.com</span>
          </div>
          <div className="top-bar-mid-mobile">
            <span>🪔 For Bulk Orders & B2B Enquiries</span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="main-header">
        <div className="container header-mid">
          <button
            className="mobile-menu-toggle-btn"
            onClick={() => {
              setIsMobileMenuOpen(!isMobileMenuOpen);
              setIsMobileSearchOpen(false);
            }}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          <div
            className="logo-image-container"
            onClick={() => onNavigate('home')}
            style={{ cursor: 'pointer' }}
          >
            <img src={logoImg} alt="Lightingale Creative Logo" className="site-logo" />
          </div>

          {/* Desktop Search Container */}
          <div className="search-container desktop-search-only" style={{ position: 'relative' }}>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', width: '100%' }}>
              <input
                type="text"
                placeholder="Search for products, collections, SKUs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
              />
              <button type="submit"><Search size={16} /></button>
            </form>

            {/* Desktop Suggestions Dropdown */}
            {isSearchFocused && searchQuery.trim().length > 0 && (
              <div className="search-suggestions-dropdown">
                {suggestions.length > 0 ? (
                  suggestions.map((item, idx) => (
                    <div
                      key={item._id || idx}
                      className="suggestion-item"
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      <img
                        src={item.img}
                        alt={item.name}
                        className="suggestion-thumb"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = SAFE_PLACEHOLDER_SVG;
                        }}
                      />
                      <div className="suggestion-details">
                        <span className="suggestion-name">{item.name}</span>
                        <span className="suggestion-type">
                          {item.type === "product"
                            ? (item.code ? `${item.code} ${item.material ? `• ${item.material}` : ''}` : "Product")
                            : "Collection"}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="suggestion-item no-results" style={{ padding: '12px', color: '#888' }}>
                    No matching products or collections found.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Icons */}
          <div className="header-actions">
            <div className="mobile-action-icons">
              {/* Only Mobile Search Icon */}
              <Search
                size={22}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setIsMobileSearchOpen(!isMobileSearchOpen);
                  setIsMobileMenuOpen(false);
                }}
              />
            </div>

            <a
              href="https://wa.me/919350824043"
              target="_blank"
              rel="noreferrer"
              className="shiny-whatsapp-hotline"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
                className="whatsapp-svg-icon"
              >
                <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
              </svg>
              <span>+91 9350824043</span>
            </a>
          </div>
        </div>
      </div>

      {/* MOBILE SEARCH OVERLAY BAR */}
      {isMobileSearchOpen && (
        <div className="mobile-search-overlay-bar">
          <form onSubmit={handleSearchSubmit} className="mobile-search-form">
            <Search size={18} className="mobile-search-icon" />
            <input
              ref={mobileInputRef}
              type="text"
              placeholder="Search lamps, collections, SKUs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              type="button"
              className="mobile-search-close-btn"
              onClick={() => {
                setIsMobileSearchOpen(false);
                setSearchQuery("");
              }}
            >
              <X size={18} />
            </button>
          </form>

          {/* Mobile Suggestions Dropdown */}
          {searchQuery.trim().length > 0 && (
            <div className="mobile-search-suggestions">
              {suggestions.length > 0 ? (
                suggestions.map((item, idx) => (
                  <div
                    key={item._id || idx}
                    className="suggestion-item"
                    onClick={() => handleSelectSuggestion(item)}
                  >
                    <img
                      src={item.img}
                      alt={item.name}
                      className="suggestion-thumb"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = SAFE_PLACEHOLDER_SVG;
                      }}
                    />
                    <div className="suggestion-details">
                      <span className="suggestion-name">{item.name}</span>
                      <span className="suggestion-type">
                        {item.type === "product"
                          ? (item.code ? `${item.code}` : "Product")
                          : "Collection"}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="suggestion-item no-results" style={{ padding: '12px', color: '#aaaaaa', fontSize: '13px' }}>
                  No matching products or collections found.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Nav Menu Bar */}
      <nav className={`nav-bar ${isMobileMenuOpen ? 'mobile-nav-open' : ''}`}>
        <div className="container nav-container-flex">
          <ul className="nav-links">
            {categories.map((category) => (
              <li key={category._id || category.slug}>
                <a
                  href={`#${category.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setIsMobileMenuOpen(false);
                    onNavigate("subcategory", { categorySlug: category.slug });
                  }}
                >
                  {getItemName(category)}
                </a>
              </li>
            ))}

            <li>
              <a
                href="#custom-design"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  onNavigate("custom-design");
                }}
              >
                Custom Design
              </a>
            </li>

            <li>
              <a
                href="#about"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  onNavigate("about");
                }}
              >
                About Us
              </a>
            </li>

            <li>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  setIsMobileMenuOpen(false);
                  onNavigate("contact");
                }}
              >
                Contact Us
              </a>
            </li>
          </ul>
          <a
            href="#b2b"
            className="b2b-btn"
            onClick={(e) => {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              onNavigate('contact');
            }}
          >
            B2B Enquiry
          </a>
        </div>
      </nav>
    </header>
  );
}